import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdminSettings, updateAdminSettings, getAdminPlans, updateAdminPlan } from '../api/admin';
import { Link } from 'react-router-dom';

export default function AdminSettingsPage() {
  return (
    <div className="space-y-8 max-w-3xl">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-gray-400 hover:text-white text-sm transition-colors">← Admin</Link>
        <h1 className="text-2xl font-bold text-white">Site Settings</h1>
      </div>
      <SiteSettingsForm />
      <StorageSection />
      <PlansEditor />
    </div>
  );
}

// ── Storage backend (global master switch) ────────────────────────────────────
function StorageSection() {
  const qc = useQueryClient();
  const { data: settings = [] } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn:  () => getAdminSettings().then((r) => r.data),
  });
  const backend = settings.find((s) => s.key === 'storage_backend')?.value || 'local';
  const setBackend = useMutation({
    mutationFn: (v) => updateAdminSettings({ storage_backend: v }),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });

  return (
    <div className="card p-6 space-y-4">
      <h2 className="font-semibold text-white">Storage</h2>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-gray-300">Where new uploads are stored</p>
          <p className="text-xs text-gray-500">
            Master switch. On <span className="text-gray-300">Google Drive (per user)</span>, each user’s uploads go to their own
            Drive if they’ve connected it in <span className="text-gray-300">their Settings</span>; otherwise they stay local.
            Existing files aren’t moved.
          </p>
        </div>
        <select className="input w-auto" value={backend} onChange={(e) => setBackend.mutate(e.target.value)}>
          <option value="local">Local disk (all users)</option>
          <option value="drive">Google Drive (per user)</option>
        </select>
      </div>
    </div>
  );
}

// ── Site settings ─────────────────────────────────────────────────────────────
const EDITABLE_SETTINGS = [
  { key: 'site_name',          label: 'Site name',            type: 'text' },
  { key: 'registrations_open', label: 'Registrations open',   type: 'toggle' },
  { key: 'sandbox_mode',       label: 'Sandbox mode (Square)',type: 'toggle' },
  { key: 'default_plan_id',    label: 'Default plan ID',      type: 'number' },
];

function SiteSettingsForm() {
  const qc                    = useQueryClient();
  const [values, setValues]   = useState({});
  const [saved,  setSaved]    = useState(false);

  const { data = [], isLoading } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn:  () => getAdminSettings().then((r) => r.data),
  });

  useEffect(() => {
    const map = {};
    data.forEach((s) => { map[s.key] = s.value; });
    setValues(map);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (patch) => updateAdminSettings(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  function handleSubmit(e) {
    e.preventDefault();
    mutation.mutate(values);
  }

  if (isLoading) return <div className="card p-8 flex justify-center"><Spinner /></div>;

  return (
    <div className="card p-6 space-y-5">
      <h2 className="font-semibold text-white">General</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {EDITABLE_SETTINGS.map(({ key, label, type }) => (
          <div key={key} className="flex items-center justify-between gap-4">
            <label className="text-sm text-gray-300 w-48 shrink-0">{label}</label>
            {type === 'toggle' ? (
              <Toggle
                value={values[key] === '1'}
                onChange={(v) => setValues({ ...values, [key]: v ? '1' : '0' })}
              />
            ) : (
              <input
                type={type}
                className="input flex-1"
                value={values[key] ?? ''}
                onChange={(e) => setValues({ ...values, [key]: e.target.value })}
              />
            )}
          </div>
        ))}

        <div className="flex items-center gap-3 pt-2">
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save settings'}
          </button>
          {saved && <span className="text-green-400 text-sm">Saved!</span>}
        </div>
      </form>
    </div>
  );
}

// ── Plans editor ──────────────────────────────────────────────────────────────
function PlansEditor() {
  const qc = useQueryClient();
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn:  () => getAdminPlans().then((r) => r.data),
  });

  const [editing, setEditing] = useState(null); // plan object being edited
  const [saved,   setSaved]   = useState(false);

  const mutation = useMutation({
    mutationFn: ({ id, ...patch }) => updateAdminPlan(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
      setEditing(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (isLoading) return null;

  const PLAN_FIELDS = [
    { key: 'name',                 label: 'Name',               type: 'text' },
    { key: 'storage_limit_mb',     label: 'Storage (MB, -1=∞)',  type: 'number' },
    { key: 'monthly_upload_limit', label: 'Monthly uploads (-1=∞)', type: 'number' },
    { key: 'max_file_size_mb',     label: 'Max file size (MB)',  type: 'number' },
    { key: 'max_folders',          label: 'Max folders (-1=∞)',  type: 'number' },
    { key: 'max_categories',       label: 'Max categories (-1=∞)', type: 'number' },
    { key: 'price_label',          label: 'Price label',         type: 'text' },
  ];

  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-gray-800 flex items-center justify-between">
        <h2 className="font-semibold text-white">Plans</h2>
        {saved && <span className="text-green-400 text-sm">Saved!</span>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-xs uppercase tracking-wider">
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-right">Storage</th>
              <th className="px-4 py-3 text-right">Monthly</th>
              <th className="px-4 py-3 text-right">File max</th>
              <th className="px-4 py-3 text-right">Price</th>
              <th className="px-4 py-3 text-center">Public</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {plans.map((p) => (
              <tr key={p.id} className="hover:bg-gray-800/50 transition-colors">
                <td className="px-4 py-3 font-medium text-white">{p.name}</td>
                <td className="px-4 py-3 text-right text-gray-300">{p.storage_limit_mb === -1 ? '∞' : `${p.storage_limit_mb} MB`}</td>
                <td className="px-4 py-3 text-right text-gray-300">{p.monthly_upload_limit === -1 ? '∞' : p.monthly_upload_limit}</td>
                <td className="px-4 py-3 text-right text-gray-300">{p.max_file_size_mb} MB</td>
                <td className="px-4 py-3 text-right text-gray-300">{p.price_label}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.is_public ? 'bg-green-900/50 text-green-400' : 'bg-gray-800 text-gray-500'}`}>
                    {p.is_public ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    className="text-brand-400 hover:text-brand-300 text-xs transition-colors"
                    onClick={() => setEditing({ ...p })}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="card w-full max-w-md p-6 space-y-4">
            <h3 className="font-semibold text-white">Edit plan: {editing.name}</h3>
            {PLAN_FIELDS.map(({ key, label, type }) => (
              <div key={key}>
                <label className="label">{label}</label>
                <input
                  type={type}
                  className="input"
                  value={editing[key] ?? ''}
                  onChange={(e) => setEditing({ ...editing, [key]: type === 'number' ? Number(e.target.value) : e.target.value })}
                />
              </div>
            ))}
            <div>
              <label className="label">Public</label>
              <Toggle
                value={!!editing.is_public}
                onChange={(v) => setEditing({ ...editing, is_public: v ? 1 : 0 })}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                className="btn-primary"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate(editing)}
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-9 h-5 rounded-full transition-colors relative ${value ? 'bg-brand-600' : 'bg-gray-700'}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

function Spinner() {
  return <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />;
}
